import { useCallback, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";

/**
 * Returns a fire-and-forget function that records the provider viewed a job.
 * The server keeps only the first view, so this just avoids repeat requests:
 * each job id is sent at most once per mount of the calling component. A
 * failed request releases the id, so the next sighting tries again; otherwise
 * the job would stay unviewed and keep getting digest reminders.
 */
export function useMarkJobPostViewed() {
  const trpc = useTRPC();
  const markedJobIdsRef = useRef(new Set<string>());
  // The release lives in the hook-level onError, which runs for every request.
  // mutate()'s own callbacks only run for the most recent call, so a failure
  // that overlaps a later card's request would otherwise never be released.
  const { mutate } = useMutation(
    trpc.jobPost.markViewed.mutationOptions({
      onError: (error, { id }) => {
        markedJobIdsRef.current.delete(id);
        console.error("Failed to mark job post viewed:", error);
      },
    }),
  );

  return useCallback(
    (jobId: string) => {
      if (markedJobIdsRef.current.has(jobId)) {
        return;
      }
      markedJobIdsRef.current.add(jobId);
      mutate({ id: jobId });
    },
    [mutate],
  );
}
